import { useAuth } from '../context/AuthContext';
import { USER_ROLES } from '../utils/constants';

/**
 * Custom hook to check granular CRUD permissions
 * @returns {Object} Permission checking functions
 */
export const usePermissions = () => {
  const { user } = useAuth();

  const currentUser = user;
  const userRole = currentUser?.role;

  /**
   * Check if user has a specific permission with a specific action
   * @param {string} permissionKey - The permission key (e.g., 'employee_management', 'recruitment')
   * @param {string} action - The CRUD action ('read', 'create', 'update', 'delete')
   * @returns {boolean} True if user has permission
   */
  const hasPermission = (permissionKey, action = 'read') => {
    // Super admin has all permissions
    if (userRole === USER_ROLES.SUPER_ADMIN) {
      return true;
    }

    // Dynamic permission check — ONLY source of truth is granularPermissions from DB
    if (!currentUser || !currentUser.granularPermissions) {
      return false;
    }

    // Normalize permission key (handle camelCase to snake_case conversion)
    const normalizedKey = typeof permissionKey === 'string' 
      ? permissionKey.replace(/([A-Z])/g, "_$1").toLowerCase()
      : permissionKey;

    // Check if permission exists in granularPermissions
    let permission = currentUser.granularPermissions[normalizedKey];
    if (!permission) {
      return false;
    }

    // Check the specific action(s)
    if (Array.isArray(action)) {
      return action.some(act => permission[act] === true);
    }

    return permission[action] === true;
  };

  /**
   * Check if user can read (view) a module
   * @param {string} permissionKey - The permission key
   * @returns {boolean}
   */
  const canRead = (permissionKey) => hasPermission(permissionKey, 'read');

  /**
   * Check if user can create in a module
   * @param {string} permissionKey - The permission key
   * @returns {boolean}
   */
  const canCreate = (permissionKey) => hasPermission(permissionKey, 'create');

  /**
   * Check if user can update in a module
   * @param {string} permissionKey - The permission key
   * @returns {boolean}
   */
  const canUpdate = (permissionKey) => hasPermission(permissionKey, 'update');

  /**
   * Check if user can delete in a module
   * @param {string} permissionKey - The permission key
   * @returns {boolean}
   */
  const canDelete = (permissionKey) => {
    const result = hasPermission(permissionKey, 'delete');
    return result;
  };

  /**
   * Get all permissions for a module
   * @param {string} permissionKey - The permission key
   * @returns {Object} Object with read, create, update, delete booleans
   */
  const getModulePermissions = (permissionKey) => {
    if (userRole === USER_ROLES.SUPER_ADMIN) {
      return { read: true, create: true, update: true, delete: true };
    }

    if (!currentUser || !currentUser.granularPermissions) {
      return { read: false, create: false, update: false, delete: false };
    }

    // Normalize permission key (handle camelCase to snake_case conversion)
    const normalizedKey = typeof permissionKey === 'string'
      ? permissionKey.replace(/([A-Z])/g, "_$1").toLowerCase()
      : permissionKey;

    let permission = currentUser.granularPermissions[normalizedKey];
    if (!permission) {
      return { read: false, create: false, update: false, delete: false };
    }

    return {
      read: permission.read === true,
      create: permission.create === true,
      update: permission.update === true,
      delete: permission.delete === true
    };
  };

  return {
    hasPermission,
    canRead,
    canCreate,
    canUpdate,
    canDelete,
    getModulePermissions,
    userRole,
    currentUser
  };
};

export default usePermissions;

